import { stored } from '../constants';

export const REVIEW_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const DRAFT_PREFIX = `${stored.reviewDraftPrefix}:`;
const SESSION_KEYS = [stored.reviewCasesFilters, stored.reviewCasesSorts, 'logInEmail'];

let draftsLocked = false;

// True once sign-out starts, so open drafts cannot be written back during the reload.
export const areReviewDraftsLocked = () => draftsLocked;

const storage = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const draftKeys = (store: Storage) =>
    Array.from({ length: store.length }, (_, index) => store.key(index))
        .filter((key): key is string => !!key && key.startsWith(DRAFT_PREFIX));

const remove = (store: Storage, key: string) => {
    try {
        store.removeItem(key);
    } catch {
        // Storage became unavailable; nothing more can be done from here.
    }
};

export const isDraftExpired = (createdAt: number, now = Date.now()) => now - createdAt >= REVIEW_DRAFT_TTL_MS;

/** Removes every value Chicago keeps in browser storage for the signed-in user. */
export const clearSessionData = () => {
    draftsLocked = true;
    const store = storage();
    if (!store) return;
    [...draftKeys(store), ...SESSION_KEYS].forEach(key => remove(store, key));
};

/**
 * Deletes expired or unreadable drafts and, once a profile is known, drafts owned by anyone else.
 * Drafts saved before expiry existed are stamped now rather than deleted.
 */
export const sweepReviewDrafts = (currentReviewerId?: number, now = Date.now()) => {
    const store = storage();
    if (!store) return;
    draftKeys(store).forEach(key => {
        const owner = key.slice(DRAFT_PREFIX.length).split(':')[0];
        if (currentReviewerId !== undefined && owner !== String(currentReviewerId)) {
            remove(store, key);
            return;
        }
        try {
            const value = JSON.parse(store.getItem(key) ?? '');
            if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid draft');
            if (value.createdAt === undefined) {
                store.setItem(key, JSON.stringify({ ...value, createdAt: now }));
            } else if (typeof value.createdAt !== 'number' || isDraftExpired(value.createdAt, now)) {
                remove(store, key);
            }
        } catch {
            remove(store, key);
        }
    });
};
